import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download } from 'lucide-react';
import { getData, setData } from '../services/storage';
import { Table } from '../components/Tables';
import { Button, Modal } from '../components/Forms';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(() => {
    const all = getData('leads') || [];
    setLeads(search ? all.filter((l) =>
      l.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.phone?.includes(search)
    ) : all);
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDelete = (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    const all = getData('leads') || [];
    setData('leads', all.filter((l) => l._id !== lead._id));
    fetchLeads();
  };

  const handleExport = () => {
    const all = getData('leads') || [];
    const csv = [
      ['Name', 'Email', 'Phone', 'Company', 'Service', 'Status', 'Date'].join(','),
      ...all.map((l) =>
        [l.name, l.email, l.phone, l.company, l.service, l.status, l.createdAt].join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'leads.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    { key: 'service', label: 'Service', sortable: true,
      render: (v) => v ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{v}</span> : '-'
    },
    { key: 'status', label: 'Status', sortable: true,
      render: (v) => {
        const colors = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-yellow-100 text-yellow-700', qualified: 'bg-purple-100 text-purple-700', converted: 'bg-green-100 text-green-700', closed: 'bg-slate-100 text-slate-500' };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[v] || 'bg-slate-100 text-slate-500'}`}>{v}</span>;
      }
    },
    { key: 'createdAt', label: 'Date', sortable: true,
      render: (v) => new Date(v).toLocaleDateString()
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm"
          />
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-slate-500 self-center">{leads.length} total</span>
          <Button variant="secondary" onClick={handleExport}>
            <Download size={18} />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table columns={columns} data={leads} onView={(lead) => setSelectedLead(lead)} onDelete={handleDelete} loading={loading} />
      </div>

      <Modal isOpen={!!selectedLead} onClose={() => setSelectedLead(null)} title="Lead Details" size="md">
        {selectedLead && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Name</label>
                <p className="text-sm font-medium text-slate-800">{selectedLead.name}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Email</label>
                <p className="text-sm font-medium text-slate-800">{selectedLead.email}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Phone</label>
                <p className="text-sm font-medium text-slate-800">{selectedLead.phone || '-'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Company</label>
                <p className="text-sm font-medium text-slate-800">{selectedLead.company || '-'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Service</label>
                <p className="text-sm font-medium text-slate-800">{selectedLead.service || '-'}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Status</label>
                <p className="text-sm font-medium text-slate-800 capitalize">{selectedLead.status}</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Message</label>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mt-1">{selectedLead.message}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Submitted</label>
              <p className="text-sm text-slate-600">{new Date(selectedLead.createdAt).toLocaleString()}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Leads;
